import { useCallback, useState } from "react";
import {
    buildCadenceDates,
    buildResetPoScheduleState,
    normalizeIsoDate,
    statefulSupplierOverride,
} from "./poSchedulePlanning";

export function usePoScheduleStore() {
    const [state, setState] = useState(() => buildResetPoScheduleState());

    const setAsOfDate = useCallback((value) => {
        setState((current) => ({
            ...current,
            asOfDate: normalizeIsoDate(value, current.asOfDate),
        }));
    }, []);

    const setGlobalScheduleDate = useCallback((index, value) => {
        setState((current) => {
            const nextGlobal = [...current.globalScheduleDates];
            const fallback =
                buildResetPoScheduleState().globalScheduleDates[index] ??
                buildResetPoScheduleState().globalScheduleDates[0];
            nextGlobal[index] = normalizeIsoDate(value, fallback);

            const nextOverrides = Object.fromEntries(
                Object.entries(current.supplierOverrides).map(([supplier, override]) => [
                    supplier,
                    statefulSupplierOverride(override, nextGlobal),
                ])
            );

            return {
                ...current,
                globalScheduleDates: nextGlobal,
                supplierOverrides: nextOverrides,
            };
        });
    }, []);

    const setSupplierFrequency = useCallback((supplier, frequency) => {
        const supplierKey = supplier.trim();
        if (!supplierKey) return;

        setState((current) => ({
            ...current,
            supplierOverrides: {
                ...current.supplierOverrides,
                [supplierKey]: {
                    frequency,
                    dates: buildCadenceDates(frequency, current.globalScheduleDates),
                },
            },
        }));
    }, []);

    const setSupplierScheduleDate = useCallback((supplier, index, value) => {
        const supplierKey = supplier.trim();
        if (!supplierKey) return;

        setState((current) => {
            const existing = statefulSupplierOverride(
                current.supplierOverrides[supplierKey],
                current.globalScheduleDates
            );
            const fallback =
                existing.dates[index] ??
                buildCadenceDates(existing.frequency, current.globalScheduleDates)[index] ??
                buildResetPoScheduleState().globalScheduleDates[0];
            const nextDates = [...existing.dates];
            nextDates[index] = normalizeIsoDate(value, fallback);

            return {
                ...current,
                supplierOverrides: {
                    ...current.supplierOverrides,
                    [supplierKey]: {
                        frequency: existing.frequency,
                        dates: nextDates,
                    },
                },
            };
        });
    }, []);

    const clearSupplierOverride = useCallback((supplier) => {
        const supplierKey = supplier.trim();
        if (!supplierKey) return;

        setState((current) => {
            const nextOverrides = { ...current.supplierOverrides };
            delete nextOverrides[supplierKey];
            return {
                ...current,
                supplierOverrides: nextOverrides,
            };
        });
    }, []);

    const resetScheduleState = useCallback(() => {
        setState(buildResetPoScheduleState());
    }, []);

    return {
        asOfDate: state.asOfDate,
        globalScheduleDates: state.globalScheduleDates,
        supplierOverrides: state.supplierOverrides,
        setAsOfDate,
        setGlobalScheduleDate,
        setSupplierFrequency,
        setSupplierScheduleDate,
        clearSupplierOverride,
        resetScheduleState,
    };
}
